import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { GridReadyEvent, ColDef, RowModelType, GridApi, GridOptions, IServerSideDatasource, IServerSideGetRowsParams, ICellRendererParams, SortModelItem } from 'ag-grid-community';
import { catchError, filter, map, of } from 'rxjs';
import { CarrierDeliveryReport } from '../models/carrier-delivery-report.model';

@Component({
  selector: 'app-carrier-delivery-report',
  templateUrl: './carrier-delivery-report.component.html',
  styleUrls: ['./carrier-delivery-report.component.scss']
})
export class CarrierDeliveryReportComponent {
  private apiUrl = '/api/carrier_delivery_reports/carrier_delivery_reports.php';

  private gridApi!: GridApi<CarrierDeliveryReport>;
  public rowData?: CarrierDeliveryReport[];
  public themeClass: string = 'ag-theme-ulaap';
  public rowModelType: RowModelType = 'serverSide';
  public cacheBlockSize = 100;
  public cacheOverflowSize = 2;
  public maxConcurrentDatasourceRequests = 2;
  public infiniteInitialRowCount = 1;
  public gridVisible = true;
  public gridOptions: GridOptions = {};

  // For filtering
  reports_form: FormGroup = this.fb.group({
    date_from: [''],
    date_to: [''],
    week_from: [''],
    week_to: [''],
  })

  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset']
    },
    autoHeight: true,
    resizable: true,
  };
  
  public columnDefs: ColDef[] =  [
    { headerName: 'Carrier Name', field: 'contact_organization', width: 300, pinned: 'left' },
    { headerName: 'Week No', field: 'week_no', filter: 'agNumberColumnFilter', valueFormatter: this.formatWeekNoCell, width: 100 },
    { headerName: 'Date', field: 'date', filter: 'agDateColumnFilter', width: 120},        
    { headerName: 'No of Cartons', field: 'no_of_cartons',  filter: false, width: 120},               
    { headerName: 'No of Cartons Delivered', field: 'no_of_cartons_delivered',  filter: false, width: 150},      
    { headerName: 'Delivery Percentage', field: 'delivery_percentage',  filter: 'agNumberColumnFilter', width: 150},     
    {
      field: 'rating', headerName: 'Delivery Rating', cellRenderer: statusTypeFormatting, filter: false, filterParams: {
        values: [
          'A',
          'B',
          'C',
          'F',
        ]
      },
    },              
    { headerName: 'Delivery Rating', field: 'rating', filter: false, width: 150 },         
    { headerName: 'Early Deliveries', field: 'early_deliveries', filter: false, width: 150 },
    { headerName: 'On Date On Time', field: 'on_date_on_time_deliveries', filter: false, width: 150 },
    { headerName: 'Late Deliveries', field: 'late_deliveries', filter: false, width: 150 },   
    { headerName: 'Late Percentage', field: 'late_percentage', filter: false, width: 150 },        
  ]

  constructor(private http: HttpClient, private fb: FormBuilder){}

  ngOnInit(){}

  onGridReady(params: GridReadyEvent<CarrierDeliveryReport>){
    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams<any, any> & { sortModel: SortModelItem[], filterModel: any}) => {
        const sortModel = params.request.sortModel;
        let filterModel = params.request.filterModel;
        filterModel = translateFilters(filterModel);

        const url = this.buildUrl(params.request.startRow || 0, params.request.endRow || 100, sortModel, filterModel);
        this.gridApi = params.api;

        // note: we use response.data.data because row data is in a key called "data" in the plugin response
        this.http.get<{ data: {data: CarrierDeliveryReport[]}, totalRows: number}>(url).subscribe(
          response => {
            params.success({rowData: response.data.data, rowCount: response.totalRows});
          },
          error => {
            console.error('error getting carrier delivery report data', error);
            params.fail();
          }
        )
      }
    }
    params.api.setGridOption('serverSideDatasource', dataSource);
  }

  // Toggle light and dark mode for grid
  toggleTheme(){
    this.themeClass = this.themeClass === 'ag-theme-ulaap' ? 'ag-theme-ulaap-dark' : 'ag-theme-ulaap';
  }

  exportToCSV() {
    var params = { columnSeparator: "|"}
    this.gridApi.exportDataAsCsv(params);
  }

  exportToExcel(){
    this.gridApi.exportDataAsExcel();
  }
  
  // Reset form values when user changes which column to filter by
  onToggleFilterChange(event: any){
    if(event.value !== 'date'){
      this.reports_form.get('date_from')?.setValue('')
      this.reports_form.get('date_to')?.setValue('')
    }
    if(event.value !== 'week'){
      this.reports_form.get('week_from')?.setValue('')
      this.reports_form.get('week_to')?.setValue('')
    }
  }

  // Combine current filter model with filter selected in header
  search(){
    let model = this.gridApi.getFilterModel();

    if(this.reports_form.get('date_from')?.value){
      const date_filter = {
        "date": {
          "filterType": "date",
          "type": "inRange",
          "dateFrom": this.formatDateAsYYYYMMDD(this.reports_form.get('date_from')?.value),
          "dateTo": this.formatDateAsYYYYMMDD(this.reports_form.get('date_to')?.value),
        }
      }
      model = Object.assign({}, date_filter, model);
    }
    else if(this.reports_form.get('week_from')?.value){
      const week_filter = {
        "week_no": {
          "filterType": "number",
          "type": "inRange",
          "filter": parseInt(this.reports_form.get('week_from')?.value ?? "0"),
          "filterTo": parseInt(this.reports_form.get('week_to')?.value ?? "52"),
        }
      }
      model = Object.assign({}, week_filter, model);
    }
    this.gridApi.setFilterModel(model);
  }

  // Reset all filters
  resetFilters(){
    this.gridApi.setFilterModel({});
    this.reports_form.get('week_from')?.setValue("");
    this.reports_form.get('week_to')?.setValue("");
    this.reports_form.get('date_from')?.setValue("");
    this.reports_form.get('date_to')?.setValue("");
  }


  // Helper function to convert user-set date for filter model
  formatDateAsYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Build get url with parameters
  private buildUrl(startRow: number, endRow: number, sortModel: SortModelItem[], filterModel: any): string {
    console.log('buildUrl() reached')
    console.log(JSON.stringify(filterModel))
    // Construct the URL with query parameters for startRow, endRow, filters, and sortModel
    const filters = encodeURIComponent(JSON.stringify(filterModel));
    const sort = encodeURIComponent(JSON.stringify(sortModel));
    return `${this.apiUrl}?startRow=${startRow}&endRow=${endRow}&filters=${filters}&sortModel=${sort}`;
  }

  openDeliveryReports(){
    window.open('/delivery_reports');
  }

  openStoreReports(){
    window.open('/store_reports');
  }

  openExceptionReports(){
    window.open('/exception_reports');
  }


  
  formatWeekNoCell(params: any): string {
    const weekNo = params.value;
    return isNaN(weekNo) ? '' : weekNo;
  }
    
}
// Cell renderer for Delivery Rating column
function statusTypeFormatting(params: ICellRendererParams) {
  let statusTypes: string[] = []
  if (params.value != null) {
    statusTypes = params.value.split(",");
  }
  let buttonsHtml = '';

  for (let type of statusTypes) {
    let trimmedType = type.trim();
    let bgColor = '';

    switch (trimmedType) {
      case 'A':
        bgColor = '#33FFFF';
        break;
      case 'B':
          bgColor = '#66FF66';
          break;
      case 'C':
            bgColor = '#FFFF66';
            break;                  
      case 'F':
        bgColor = '#FF6666';
        break;
      default:
        bgColor = '#FFFFFF';
    }

    buttonsHtml += '<button class="btn dropdown-toggle button-text-wrapper" style="background-color:' + bgColor + ';border-radius:10px;font-weight:bold;">&nbsp;&nbsp;' + trimmedType + '&nbsp;&nbsp;</button> <br>';
  }

  let element = '<div class="btn-group">' + buttonsHtml + '</div>';

  return element;
}

// Changes column name to be understandable to plugin
// Swaps filter ranges in case of user error
function translateFilters(filterModel: any){
  const aliasMapping: {[key: string]: string} = {
    //col_alias: table_name.col_name
    "week_no": "shipment_list.week_no",
    "date": "shipment_list.date"
  }
  const translatedFilters: {[key: string]: string} = {}

  for(const alias in filterModel){
    // switches date values if needed
    if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'date'){
      let startDate = new Date(filterModel[alias].dateFrom);
      let endDate = new Date(filterModel[alias].dateTo);
      if(startDate > endDate){
        const dateHolder = filterModel[alias].dateTo;
        filterModel[alias].dateTo = filterModel[alias].dateFrom;
        filterModel[alias].dateFrom = dateHolder;
      }
    }
    // switch number values if needed
    if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'number' && filterModel[alias].filterTo < filterModel[alias].filter){
      const numHolder = filterModel[alias].filterTo;
      filterModel[alias].filterTo = filterModel[alias].filter;
      filterModel[alias].filter = numHolder;
    }
    // move filterModel data into new filter model with updated alias
    const qualifiedName: string = aliasMapping[alias] || alias;
    translatedFilters[qualifiedName] = filterModel[alias];
  }
  return translatedFilters;
}